import { Button, Descriptions, type DescriptionsProps, Divider, Flex, Spin, Typography } from 'antd'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'

import { MfaSchoolRequirementService } from '@services/MfaSchoolRequirementService'
import { useConfirmation } from '@store/confirmation'

import type { Invitee, Product, School, Subscription } from '../../../../types'
import { EditSchoolDetailsModal } from '../../../components/EditSchoolDetailsModal'
import AddSubscriptionModal from '../components/AddSubscriptionModal'
import { EditSubscriptionModal } from '../components/EditSubscriptionModal'
import { MfaDetailsModal } from '../components/MfaDetailsModal'
import { SeatChangeModal } from '../components/SeatChangeModal'
import { SubscriptionCard } from '../components/SubscriptionCards'

export const SchoolDetailsTab = ({
    fetch,
    products,
    school,
    invitees
}: {
    fetch: () => Promise<void>
    school: School
    products: Product[]
    invitees: Invitee[]
}) => {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [isAddSubscriptionModalOpen, setIsAddSubscriptionModalOpen] = useState(false)
    const [isMfaDetailModalOpen, setIsMfaDetailModalOpen] = useState(false)
    const [subscriptionToChangeSeats, setSubscriptionToChangeSeats] = useState<Subscription | null>(null)
    const [subscriptionToEdit, setSubscriptionToEdit] = useState<Subscription | null>(null)
    const confirm = useConfirmation()

    const closeEditModal = () => {
        setIsEditModalOpen(false)
    }

    const items: DescriptionsProps['items'] = [
        {
            key: '2',
            label: 'Name',
            children: school?.name
        },
        {
            key: 'email',
            label: 'Email',
            children: school?.email || 'N/A'
        },
        {
            key: 'domain',
            label: 'Domain',
            children: school?.domain || 'N/A'
        },
        {
            key: 'country',
            label: 'Country',
            children: school?.country
        },
        {
            key: 'address',
            label: 'Address',
            children: school?.address || 'N/A'
        },
        {
            key: 'state',
            label: 'State',
            children: school?.state || 'N/A'
        },
        {
            key: 'city',
            label: 'City',
            children: school?.city || 'N/A'
        },
        {
            key: 'postalCode',
            label: 'Postal Code',
            children: school?.postalCode || 'N/A'
        },
        {
            key: 'phone',
            label: 'Phone',
            children: school?.phone || 'N/A'
        },
        {
            key: 'schoolId',
            label: 'School ID',
            children: school?.id
        },
        {
            key: 'stripeId',
            label: 'Stripe ID',
            children: school?.stripeId
        },
        {
            key: 'createdAt',
            label: 'Created At',
            children: school?.createdAt ? dayjs(school.createdAt).format('YYYY-MM-DD HH:mm:ss') : 'N/A'
        },
        {
            key: 'updatedAt',
            label: 'Updated At',
            children: school?.updatedAt ? dayjs(school.updatedAt).format('YYYY-MM-DD HH:mm:ss') : 'N/A'
        }
    ]

    const inviteeCounts = useMemo(() => {
        return invitees.reduce((collector: { [key: string]: number }, invitee) => {
            if (invitee.claims) {
                invitee.claims.forEach(claim => {
                    if (claim.schoolSubscriptionId) {
                        if (!collector[claim.schoolSubscriptionId]) {
                            collector[claim.schoolSubscriptionId] = 0
                        }
                        collector[claim.schoolSubscriptionId] += 1
                    }
                })
            }

            return collector
        }, {})
    }, [invitees])

    const onDeleteMfaConfig = async (id: string) => {
        if (
            await confirm({
                title: 'Delete MFA Configuration',
                description:
                    'Please confirm that you wish to remove all MFA requirements for this school and its users.'
            })
        ) {
            await MfaSchoolRequirementService.delete(id)
            await fetch()
        }
    }

    if (!school) {
        return <Spin />
    }

    return (
        <>
            <Descriptions
                bordered
                title="Informations"
                items={items}
                column={{ xs: 1, sm: 1, md: 1, lg: 2, xl: 3, xxl: 3 }}
                extra={<Button onClick={() => setIsEditModalOpen(true)}>Edit</Button>}
            />
            <Divider />

            <Flex justify="space-between" align="center">
                <Typography.Title level={4}>Subscriptions</Typography.Title>
                <Button type="primary" onClick={() => setIsAddSubscriptionModalOpen(true)}>
                    + New Subscription
                </Button>
            </Flex>
            <Flex>
                <Flex gap="small" wrap>
                    {school.schoolSubscriptions.map(({ subscription }) => {
                        const schoolSubscription = school.schoolSubscriptions.find(
                            ss => ss.subscriptionId === subscription.id
                        )

                        const seatCount = schoolSubscription?.seatCount || 0
                        const usedSeats = schoolSubscription?.schoolSubscriptionSeats?.length || 0
                        const invitedSeats =
                            schoolSubscription && inviteeCounts[schoolSubscription.id]
                                ? inviteeCounts[schoolSubscription.id]
                                : 0

                        return (
                            <SubscriptionCard
                                school={school}
                                refetch={fetch}
                                key={subscription.id}
                                subscription={subscription}
                                products={products}
                                seatCount={seatCount}
                                usedSeats={usedSeats}
                                invitedSeats={invitedSeats}
                                onEditSubscription={() => setSubscriptionToEdit(subscription)}
                                onEditSeats={() => setSubscriptionToChangeSeats(subscription)}
                            />
                        )
                    })}
                </Flex>
            </Flex>
            <Flex vertical>
                <Flex justify="space-between" align="center">
                    <Typography.Title level={4}>MFA</Typography.Title>
                    <Flex gap="middle">
                        <Button onClick={() => setIsMfaDetailModalOpen(true)}>
                            {school.mfaSchoolRequirement ? 'Edit' : 'Add'}
                        </Button>
                        {school.mfaSchoolRequirement && (
                            <Button onClick={() => onDeleteMfaConfig(school.mfaSchoolRequirement.id)}>Delete</Button>
                        )}
                    </Flex>
                </Flex>
                {school.mfaSchoolRequirement && <pre>{JSON.stringify(school.mfaSchoolRequirement, null, 2)}</pre>}
            </Flex>

            {isEditModalOpen && (
                <EditSchoolDetailsModal
                    isOpen={isEditModalOpen}
                    school={school}
                    onClose={closeEditModal}
                    refetch={fetch}
                />
            )}
            {isAddSubscriptionModalOpen && (
                <AddSubscriptionModal
                    isOpen={isAddSubscriptionModalOpen}
                    school={school}
                    products={products}
                    onClose={() => setIsAddSubscriptionModalOpen(false)}
                    refetch={fetch}
                />
            )}
            {isMfaDetailModalOpen && (
                <MfaDetailsModal
                    school={school}
                    isOpen={isMfaDetailModalOpen}
                    onClose={() => setIsMfaDetailModalOpen(false)}
                    refetch={fetch}
                />
            )}
            {!!subscriptionToEdit && (
                <EditSubscriptionModal
                    isOpen={!!subscriptionToEdit}
                    subscription={subscriptionToEdit}
                    products={products}
                    refetch={fetch}
                    school={school}
                    seatCount={
                        (subscriptionToEdit &&
                            school.schoolSubscriptions.find(ss => ss.subscriptionId === subscriptionToEdit?.id)
                                ?.seatCount) ||
                        0
                    }
                    onClose={() => setSubscriptionToEdit(null)}
                />
            )}
            {!!subscriptionToChangeSeats && (
                <SeatChangeModal
                    isOpen={!!subscriptionToChangeSeats}
                    subscription={subscriptionToChangeSeats}
                    products={products}
                    refetch={fetch}
                    school={school}
                    seatCount={
                        (subscriptionToChangeSeats &&
                            school.schoolSubscriptions.find(ss => ss.subscriptionId === subscriptionToChangeSeats?.id)
                                ?.seatCount) ||
                        0
                    }
                    onClose={() => setSubscriptionToChangeSeats(null)}
                />
            )}
        </>
    )
}
